import { EntityDetailsWrapper, WebServiceConfigJSON, WebServiceSetting } from 'components';
import { WebServiceEndpoints } from 'components/WebServices/WebServiceEndpoints';
import { WebServiceActionTypes, WebServiceContext } from 'contexts/WebServiceContext';
import { CACHE_FIRST, ENTITY_PATH_ID } from 'contracts/constants';
import type { EntityTab } from 'contracts/interfaces';
import { isEqual } from 'lodash';
import type { FC } from 'react';
import { useContext, useEffect } from 'react';
import { Prompt, useLocation } from 'react-router-dom';
import { useGetWebServiceQuery } from 'services/graphql/generated/hooks';
import { getParams } from 'utils';

interface WebServiceProps {
  isEditable: boolean;
}
const WebService: FC<WebServiceProps> = ({ isEditable }) => {
  const { pathname, hash } = useLocation();
  const params: any = getParams(pathname, [ENTITY_PATH_ID]);
  const ctx = useContext(WebServiceContext);
  const {
    loading: loadingWebService,
    data: webServiceData,
    refetch
  } = useGetWebServiceQuery({
    variables: { packageVersionUuid: params.versionUuid, uuid: params.entityId },
    ...CACHE_FIRST
  });

  useEffect(() => {
    if (webServiceData?.getWebService.schema) {
      ctx?.dispatch({
        type: WebServiceActionTypes.INIT_WEB_SERVICE_CONFIG,
        payload: {
          schema: webServiceData.getWebService.schema,
          uuid: webServiceData.getWebService.uuid,
          name: webServiceData.getWebService.name,
          description: webServiceData.getWebService.description || undefined,
          packageVersionUuid: webServiceData.getWebService.packageVersionUuid
        }
      });
    }
  }, [webServiceData]);

  const tabs: EntityTab[] = [
    {
      label: 'Settings',
      isActive: !hash || hash === '#settings',
      content: <WebServiceSetting params={params} isEditable={isEditable} />
    },
    {
      label: 'Endpoints',
      isActive: hash === '#endpoints',
      content: (
        <WebServiceEndpoints
          params={params}
          isEditable={isEditable}
          service={{
            name: webServiceData?.getWebService.name,
            uuid: webServiceData?.getWebService.uuid,
            schema: webServiceData?.getWebService.schema
          }}
        />
      )
    },
    {
      label: 'JSON',
      isActive: hash === '#json',
      content: <WebServiceConfigJSON />
    }
  ];

  return (
    <>
      <Prompt
        when={isEditable && !isEqual(webServiceData?.getWebService.schema, ctx?.state.schema)}
        message={(location, action) => `Changes that you made will not be saved.`}
      />
      <EntityDetailsWrapper key={`${params.versionUuid}_${params.entityId}`} tabs={tabs} />
    </>
  );
};

export default WebService;
